import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@branditdev/ui'
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Button, Spinner, FormGroup, Label, Input, ButtonGroup } from 'reactstrap'
import { fetchAdminUserOrderApprovals } from '../../actions'
import { formatDate } from '../../util/utils'
import dollar_price from '../general/DollarPrice'
import OrderDetailsModal from './OrderDetailsModal'

const isPendingOrder = order => order?.order_status === 'Pending'

const AdminUserDashboardOrders = ({ portalId }) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.currentUser)
  const selectedPortalId = portalId
  const orders = useSelector(state => state.adminUser.orders || [])
  const totalCount = useSelector(state => state.adminUser.totalCount || 0)

  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [modalType, setModalType] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sorting, setSorting] = useState([])
  const pageSize = 25

  const [selectedStatus, setSelectedStatus] = useState('Pending')

  const handleStatusChange = newStatus => {
    if (newStatus !== selectedStatus) {
      setSelectedStatus(newStatus)
      setCurrentPage(1)
      setSearchQuery('')
    }
  }

  // Function to fetch orders with search
  const fetchOrders = useCallback(
    async (skip = 0, search = '') => {
      if (!selectedPortalId) return

      setLoading(true)
      try {
        await dispatch(fetchAdminUserOrderApprovals(selectedPortalId, skip, pageSize, selectedStatus, search))
      } catch {
        toast.error('Sorry! There was an issue fetching orders.')
      } finally {
        setLoading(false)
      }
    },
    [selectedPortalId, selectedStatus, dispatch]
  )

  // Fetch orders when status, page, or search changes
  useEffect(() => {
    if (selectedPortalId) {
      const skip = (currentPage - 1) * pageSize
      fetchOrders(skip, searchQuery)
    }
  }, [selectedPortalId, currentPage, selectedStatus, searchQuery, fetchOrders])

  const handleSearchChange = useCallback(value => {
    setSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const nextPage = () => setCurrentPage(prev => prev + 1)
  const prevPage = () => setCurrentPage(prev => (prev > 1 ? prev - 1 : prev))
  const firstPage = () => setCurrentPage(1)
  const totalPages = Math.ceil(totalCount / pageSize)
  const lastPage = () => setCurrentPage(totalPages)

  const openModal = (order, type) => {
    setSelectedOrder(order)
    setModalType(type)
  }

  const closeModal = () => {
    setSelectedOrder(null)
    setModalType(null)
  }

  const handleApprove = () => {
    // Refresh the orders list after approval
    if (selectedPortalId) {
      const skip = (currentPage - 1) * pageSize
      dispatch(fetchAdminUserOrderApprovals(selectedPortalId, skip, pageSize, selectedStatus, searchQuery))
    }
  }

  const handleDeny = () => {
    // Refresh the orders list after denial
    if (selectedPortalId) {
      const skip = (currentPage - 1) * pageSize
      dispatch(fetchAdminUserOrderApprovals(selectedPortalId, skip, pageSize, selectedStatus, searchQuery))
    }
  }

  const columns = useMemo(
    () => [
      { header: 'ID', accessorKey: 'id' },
      { header: 'Order Number', accessorKey: 'order_number' },
      { header: 'User Name', accessorKey: 'user_name' },
      { header: 'Portal Name', accessorKey: 'portal_name' },
      {
        header: 'Total Cost',
        accessorKey: 'total_cents',
        cell: info => dollar_price(info.getValue())
      },
      { header: 'Status', accessorKey: 'order_status' },
      {
        header: 'Created At',
        accessorKey: 'created_at',
        cell: info => formatDate(info.getValue())
      },
      {
        header: 'Actions',
        cell: ({ row }) => {
          const canReview = isPendingOrder(row.original)

          return (
            <>
              <Button color="link" onClick={() => openModal(row.original, 'view')}>
                View
              </Button>
              <Button color="link" disabled={!canReview} onClick={() => openModal(row.original, 'approve')}>
                Approve
              </Button>
              <Button color="link" disabled={!canReview} onClick={() => openModal(row.original, 'deny')}>
                Deny
              </Button>
            </>
          )
        }
      }
    ],
    []
  )

  const table = useReactTable({
    data: orders,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  return (
    <div className="container mt-4 admin-user-orders-dashboard">
      <h4>Orders Review</h4>
      <div className="my-3 text-center">
        <ButtonGroup>
          <Button
            color="primary"
            outline
            active={selectedStatus === 'Pending'}
            onClick={() => handleStatusChange('Pending')}
          >
            Pending
          </Button>
          <Button
            color="primary"
            outline
            active={selectedStatus === 'Approved'}
            onClick={() => handleStatusChange('Approved')}
          >
            Approved
          </Button>
          <Button
            color="primary"
            outline
            active={selectedStatus === 'Cancelled'}
            onClick={() => handleStatusChange('Cancelled')}
          >
            Denied
          </Button>
        </ButtonGroup>
      </div>
      <FormGroup className="mt-3">
        <Label for="search">Search Orders</Label>
        <Input
          id="search"
          type="text"
          value={searchQuery}
          onChange={e => handleSearchChange(e.target.value)}
          placeholder="Search orders..."
        />
      </FormGroup>

      {loading && (
        <div className="text-center my-4">
          <Spinner color="primary" />
          <p>Loading orders...</p>
        </div>
      )}

      {!loading && orders.length === 0 && (
        <p className="text-muted">
          {searchQuery ? `No orders found` : `No ${selectedStatus.toLowerCase()} orders found.`}
        </p>
      )}

      {!loading && orders.length > 0 && (
        <>
          <Table className="mt-3">
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableHead
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <Button color="primary" onClick={firstPage} disabled={currentPage === 1} aria-label="First">
                  &laquo; First
                </Button>{' '}
                <Button color="primary" onClick={prevPage} disabled={currentPage === 1} aria-label="Previous">
                  &lt; Previous
                </Button>{' '}
                <Button color="primary" onClick={nextPage} disabled={currentPage >= totalPages} aria-label="Next">
                  Next &gt;
                </Button>{' '}
                <Button color="primary" onClick={lastPage} disabled={currentPage >= totalPages} aria-label="Last">
                  Last &raquo;
                </Button>
              </div>
              <div>
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}

          <OrderDetailsModal
            key={`${selectedOrder?.id || 'none'}-${modalType || 'closed'}`}
            isOpen={!!modalType}
            toggle={closeModal}
            orderId={selectedOrder?.id}
            modalType={modalType}
            onApprove={handleApprove}
            onDeny={handleDeny}
            initialOrder={selectedOrder}
            currentUser={currentUser}
          />
        </>
      )}
    </div>
  )
}

AdminUserDashboardOrders.propTypes = {
  portalId: PropTypes.number
}

export default AdminUserDashboardOrders
